/**
 * ConnectionNotice — the "Lost connection!!" banner for the Fun Challenges
 * lobby + battle screens.
 *
 * This complements the `ConnectionModal` (which is driven by the
 * `change_client_connection` message). When ANOTHER client — e.g. the mobile
 * app — takes over the shared socket, the displacement reaches this tab as a
 * socket-level `disconnect`/`close` (not always the change_client message), so
 * without this the Fun Challenges tab had no way to know it had gone stale.
 * PlayChess already shows the exact same NotificationBox off its `isConnected`
 * flag; this gives Fun Challenges parity.
 */
import React, {useEffect, useState} from 'react';

import NotificationBox from '@/Screens/PlayChess/components/NotificationBox';
import {styles as playChessStyles} from '@/Screens/PlayChess/styles';
import {useSocketConnected} from '@/Hooks/UseSocket/useSocketConnected';

// Match PlayChess's 1s grace so a momentary blip doesn't flash the banner.
const GRACE_MS = 1000;

const ConnectionNotice: React.FC = () => {
  const connected = useSocketConnected();
  const [show, setShow] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (!connected) {
      timeout = setTimeout(() => setShow(true), GRACE_MS);
    } else {
      setShow(false);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [connected]);

  if (connected || !show) return null;
  return <NotificationBox styles={playChessStyles} />;
};

export default ConnectionNotice;
